import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";

export const Work = () => {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(null);

  return (
    <section id="work" className="border-t border-white/10 bg-card/40" data-testid="work-section">
      <div className="mx-auto max-w-[1600px] px-4 py-24 sm:px-8 sm:py-32 lg:px-16">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent sm:text-sm">{t.work.eyebrow}</p>
          <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-ink sm:text-5xl">
            {t.work.title}
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2">
          {t.work.items.map((item, i) => (
            <Reveal key={item.id} delay={(i % 2) * 0.12}>
              <button
                onClick={() => setSelected(item)}
                className={`group block w-full text-left ${i % 2 === 1 ? "md:mt-16" : ""}`}
                data-testid={`work-card-${item.id}`}
              >
                <div className="relative overflow-hidden rounded-xl border border-white/10">
                  <img
                    src={item.image}
                    alt={item.client}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover grayscale-[60%] transition-all duration-700 ease-out group-hover:scale-[1.04] group-hover:grayscale-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-canvas/80 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-40" />
                  <span className="absolute right-4 top-4 rounded-full border border-accent/40 bg-canvas/70 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-accent backdrop-blur-md">
                    {item.year}
                  </span>
                  <span className="absolute bottom-4 left-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink opacity-0 transition-all duration-500 group-hover:opacity-100">
                    {t.work.viewCase} <ArrowUpRight className="h-4 w-4 text-accent" />
                  </span>
                </div>
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-ink transition-colors duration-300 group-hover:text-accent sm:text-2xl">
                      {item.client}
                    </h3>
                    <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-dim">{item.category}</p>
                  </div>
                  <span className="mt-1 rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-mist">
                    {item.result}
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-canvas/80 p-4 backdrop-blur-md"
            onClick={() => setSelected(null)}
            data-testid="work-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-white/10 bg-card"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img src={selected.image} alt={selected.client} className="aspect-[16/9] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                <button
                  onClick={() => setSelected(null)}
                  className="absolute right-4 top-4 rounded-full border border-white/20 bg-canvas/70 p-2 text-ink backdrop-blur-md transition-colors hover:border-accent hover:text-accent"
                  data-testid="work-modal-close-button"
                  aria-label={t.work.close}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-8 sm:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent">{selected.category} — {selected.year}</p>
                <h3 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-ink sm:text-4xl">{selected.client}</h3>
                <p className="mt-5 leading-relaxed text-mist">{selected.summary}</p>
                <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-accent/40 bg-accent/10 px-5 py-2.5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">{t.work.resultLabel}</span>
                  <span className="font-display text-sm font-bold uppercase text-ink">{selected.result}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
